//third-party
import { multiply, add } from "dinero.js";
import { LocalDate  } from "@js-joda/core";
import { Card, Form, ListGroup, Placeholder } from "react-bootstrap";
import React from "react";

//spotsync
import { Money, showMoney, showMoneyShort, cents } from "../Data/Money";
import { showDate } from "../Data/Date";
import { ParkerPlanInvite, SubscribeInvitePreviewResp } from "../Data/ApiTypes";
import { AddSubtract } from "./AddSubtract";
import { useState, useEffect } from "react";
import { Api } from "../Data/Api";
import { MonthlyPriceBreakdown } from "./MonthlyPriceBreakdown";
import { isPreviewProrated } from "../Data/Common";
import { OptInFeesSelector, OptInFeesSelection } from "./OptInFeesSelector";
import { DataEntitiesFinanceOperatorFeeScope } from "../shared/api/axios-client";

interface MonthlyParkingInviteDetailsProps {
    color?:         string;    
    invite:         ParkerPlanInvite;
    disabled?:      boolean;
    quantity?:      number;
    setQuantity?:   ( qty: number ) => void;
    isAch :         boolean;
    /**Callback when opt-in fees selection changes */
    onOptInFeesChange?: (selection: OptInFeesSelection) => void;
}

export function MonthlyParkingInviteDetails( props: MonthlyParkingInviteDetailsProps ) {
    const { color = "primary",
        invite,
        quantity,
        setQuantity,
        disabled = false,
        isAch = false
    } = { ...props };

    const [isLoading, setIsLoading] = useState(true);
    const [proratedFee, setProratedFee] = useState<Money>();
    const [previewData, setPreviewData] = useState<SubscribeInvitePreviewResp | null>(null);
    const [optInFeesSelection, setOptInFeesSelection] = useState<OptInFeesSelection>({});

    const handleOptInFeesSelectionChange = (selection: OptInFeesSelection) => {
        setOptInFeesSelection(selection);
        props.onOptInFeesChange?.(selection);
    };

    async function subscribePreview() {
        setIsLoading(true);
        const selectedOptInFeeIds = Object.keys(optInFeesSelection)
            .filter(id => optInFeesSelection[parseInt(id)])
            .map(id => parseInt(id));
        const res = await Api.subscriptionInvitePreview( invite.listedPlan.planId, {
            parkerPlanInviteId: invite.parkerPlanInviteId,
            start:              invite.startDate,
            quantity:           quantity!,
            isAch:              isAch,
            selectedOptInFees:  selectedOptInFeeIds.length > 0 ? selectedOptInFeeIds : undefined
        } );
        if( res.isOk ) {
            setPreviewData( res.value );
            setProratedFee( res.value.price );          
        }
        setIsLoading(false);
    }

    useEffect( () => {
        subscribePreview();
    }, [quantity, optInFeesSelection, isAch] );

    const monthlyRate = multiply(invite.privatePrice, quantity ?? 1);
    return <Card className={`border-2 border-${color} my-1`}>
        <Card.Header className={`text-center bg-${color}-subtle`}>
            Monthly Parking Options
        </Card.Header>
        <Card.Body>            
            <ListGroup.Item>
                <div className="d-flex flex-row border rounded-4 p-3 m-2 fs-7">
                    <Form.Check
                        className="m-0 flex-grow-1"
                        type="radio"
                        label={`${invite.listedPlan.name}`}
                        checked={true}
                        inline
                        readOnly />
                    <span className="fw-bold">
                        {showMoneyShort(invite.listedPlan.price)}
                    </span>
                </div>
            </ListGroup.Item>
            
            <div className="mt-2 w-auto gap-3" style={{ display: "inline-grid", gridTemplateColumns: "auto 1fr 1fr" }}>
                {(quantity !== undefined && setQuantity !== undefined) && <>
                    <div className="text-start d-flex align-items-center">
                        Spots
                    </div>
                    <div className="text-end">
                        <div className="d-inline-flex align-items-center justify-content-start justify-content-md-center gap-1">
                            <AddSubtract quantity={quantity} set={qty => disabled !== true && setQuantity(qty)} />
                        </div>
                    </div>
                    <div></div>
                </>}

                <div className="text-start">Start Date</div>
                <div className="text-end">{showDate(invite.startDate)}</div>
                <div></div>

                <div className="text-start">Monthly Rate</div>
                <div className="text-end fw-bold">{showMoney(monthlyRate)}</div>
                <div></div>

                <div style={{ gridColumn: "1 / 4" }}>
                        <OptInFeesSelector
                            disabled={disabled}
                            facilityId={invite.facilityId}
                            feeScope={DataEntitiesFinanceOperatorFeeScope.NUMBER_1} // 1 for monthly parking
                            feePaymentType={props.isAch ? 1 : 0} // ACH vs Card payment type
                            onSelectionChange={handleOptInFeesSelectionChange}
                            isCheckout={true}
                        />
                    </div>

                <div style={{ gridColumn: "1 / 4", width: "100%" }}>
                    {isLoading ? (
                        <div className="border rounded p-2">
                            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.5rem" }}>
                                <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={6} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={4} /></Placeholder>
                                <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={4} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={3} /></Placeholder>
                                <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={5} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={3} /></Placeholder>
                            </div>
                        </div>
                    ) : previewData && (
                        <div className="fade-in">
                            <MonthlyPriceBreakdown 
                                price={previewData.price}
                                fees={previewData.fees}
                                showTotalLabel="Due Today"
                                showProratedHint={isPreviewProrated(previewData)}
                            />
                        </div>
                    )}
                </div>
            </div>
        </Card.Body>
    </Card>;
}